import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import chartUtils from '../../util/chartUtils';

export function ViewSwitch({ views, active, onChange, wallmode }) {
  const shown = wallmode ? views.filter(view => view.id !== 'list') : views;
  if (shown.length < 2) {
    return null;
  }
  return (
    <div className="view-switch" role="group">
      {shown.map(({ id, icon }) => (
        <Button
          key={id}
          variant='secondary'
          size='sm'
          className={`btn-action ${active === id ? 'active' : ''}`}
          onClick={() => onChange(id)}
        >
          <img src={icon} alt={id} />
        </Button>
      ))}
    </div>
  );
}

ViewSwitch.propTypes = {
  views: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, icon: PropTypes.string })),
  active: PropTypes.string,
  onChange: PropTypes.func,
  wallmode: PropTypes.bool
};

export function CardList({ columns, rows, loading, keyDots }) {
  if (!rows.length) {
    return (
      <div className="view-list view-list--empty">
        <div
          className={`chart-placeholder chart-placeholder--table ${loading ? 'chart-placeholder--loading' : ''}`}
          dangerouslySetInnerHTML={{ __html: chartUtils.placeholderMarkup('table', loading) }}
        />
      </div>
    );
  }
  return (
    <div className={`view-list ${columns.length === 1 ? 'view-list--single' : ''}`}>
      <div className="view-list-row view-list-header">
        <span />
        <span />
        {columns.map(column => <span key={column} className="view-list-number">{column}</span>)}
      </div>
      {/* the same column names for the list's second half, shown only when a card lays its rows out in two halves */}
      <div className="view-list-row view-list-header view-list-header--twin" aria-hidden="true">
        <span />
        <span />
        {columns.map(column => <span key={column} className="view-list-number">{column}</span>)}
      </div>
      {rows.map(row => (
        <div className="view-list-row" key={row.name}>
          <span className="view-list-dot" style={keyDots ? chartUtils.keyDotStyle(row.color) : { background: row.color }} />
          <span className="view-list-name">{row.name}</span>
          {row.values.map((value, i) => (
            <span key={i} className="view-list-number">{value}</span>
          ))}
        </div>
      ))}
    </div>
  );
}

CardList.propTypes = {
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.string),
  rows: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, color: PropTypes.string, values: PropTypes.array })),
  keyDots: PropTypes.bool
};

// TODO: render the shapes as JSX instead of an HTML string. placeholderMarkup builds
// strings for togglePlaceholder's imperative DOM hook; React callers only need the markup,
// and inherit dangerouslySetInnerHTML plus its shape allowlist for no reason.
export function ChartPlaceholder({ shape, loading, textless }) {
  const wordless = loading || textless;
  return (
    <div
      className={`chart-placeholder chart-placeholder--${shape} ${loading ? 'chart-placeholder--loading' : ''}`}
      aria-hidden={wordless ? 'true' : undefined}
      dangerouslySetInnerHTML={{ __html: chartUtils.placeholderMarkup(shape, wordless) }}
    />
  );
}

ChartPlaceholder.propTypes = {
  shape: PropTypes.string,
  loading: PropTypes.bool,
  textless: PropTypes.bool
};

export function PieLegend({ data, hovered, hidden, onHover, onToggle, offset = 0 }) {
  const rows = data;
  const shares = chartUtils.wholeShares(rows.map(item => (hidden.includes(item.name) ? 0 : item.y || 0)));
  return (
    <ul className="pie-legend" style={offset ? { '--pie-names-offset': `${offset}px` } : undefined}>
      {rows.map((item, index) => (
        <li
          key={item.name}
          role="button"
          className={`pie-legend-row ${hovered === item.name ? 'is-hovered' : ''} ${hidden.includes(item.name) ? 'is-hidden' : ''}`}
          onMouseEnter={() => onHover(item.name, true)}
          onMouseLeave={() => onHover(item.name, false)}
          onClick={() => onToggle(item.name)}
        >
          <span className="pie-legend-dot" style={chartUtils.keyDotStyle(item.color)} />
          {!hidden.includes(item.name) && <span className="pie-legend-share">{shares[index]}</span>}
          <span className="pie-legend-name">{item.name}</span>
        </li>
      ))}
    </ul>
  );
}

PieLegend.propTypes = {
  data: PropTypes.array,
  hovered: PropTypes.string,
  hidden: PropTypes.arrayOf(PropTypes.string),
  onHover: PropTypes.func,
  onToggle: PropTypes.func,
  offset: PropTypes.number
};

function pieSlice(chart, name) {
  const series = chart && chart.series[0];
  return series && series.points ? series.points.find(item => item.name === name) : null;
}

export function lightSlice(chart, name, on) {
  const point = pieSlice(chart, name);
  if (!point || point.visible === false) {
    return false;
  }
  point.setState(on ? 'hover' : '');
  chartUtils.liftSlice(point, on);
  return true;
}

export function switchSlice(chart, name) {
  const point = pieSlice(chart, name);
  if (point) {
    point.setVisible(!point.visible);
  }
}

export function toggledNames(hidden, name) {
  return hidden.includes(name) ? hidden.filter(item => item !== name) : [...hidden, name];
}

export function rangeTitle(dates, format) {
  if (!dates || !dates.startDate || !dates.endDate || !format) {
    return '';
  }
  return chartUtils.dateRange(moment(dates.startDate), moment(dates.endDate), format);
}
